/**
 * Cart drawer — auto-gift controller (window-level singleton).
 *
 * Mirrors the progress bar. Reads tier config from
 *   <cart-drawer-auto-gift>
 *     <script type="application/json" data-auto-gift-config>{...}</script>
 *   </cart-drawer-auto-gift>
 * rendered server-side, and on every cart event refetches /cart.js, computes
 * qualifying spend with window.qualifyingSpendCentsFromCart (same function the
 * progress bar uses), and guarantees the right gift lines exist:
 *
 *   • spend ≥ threshold ∧ no gift line for tier   → POST /cart/add
 *   • spend ≥ threshold ∧ extra gift lines        → remove extras
 *   • spend  < threshold ∧ gift line(s) for tier  → remove all
 *
 * Rejection handling: clicking × on a gift line (cart.js calls
 * __autoGiftRejectTier) sets a session-scoped flag for that tier. The
 * controller honors the flag and skips the add branch even when threshold
 * is met — so removing the gift actually stays removed instead of
 * reappearing on the next event. The flag clears the moment spend drops
 * below the tier's threshold (or the cart empties), giving the shopper a
 * fresh chance the next time they re-cross.
 *
 * Triggers:
 *   • document `cart:updated`
 *   • pub-sub `cart-update`
 *   • initial page load (DOMContentLoaded)
 *
 * Concurrency: one reconcile in-flight at a time. Subsequent triggers set a
 * pending flag; the drain loop runs another iteration after the current one
 * settles. /cart.js is refetched at the start of each iteration so the
 * controller always works against authoritative state.
 */
(function () {
  'use strict';

  let _inFlight = false;
  let _pending = false;
  // Tier config is static per page load. Cache the first successful read so
  // a transient AJAX section render that drops the marker element (empty
  // cart, lost overlay-group block context) can't leave the controller
  // configless — the next reconcile keeps using the cached tiers and the
  // gift can re-add when the threshold is re-met.
  let _cachedConfigs = null;
  // Per-tier previous gift count so we can infer manual removal even if a
  // specific remove-button integration misses calling __autoGiftRejectTier.
  // key: blockId:tierIndex -> previous existing gift line count
  const _prevGiftCounts = new Map();

  // In-page rejection flags. If a shopper removes a gift, keep it removed
  // for the current page session so it doesn't immediately re-add. A full
  // navigation/reload resets this state. Flags are also cleared when cart
  // empties or spend drops below the threshold.
  const _rejectedTiers = new Set();
  function rejectionKey(blockId, tierIndex) {
    return 'autoGiftRejected:' + blockId + ':' + tierIndex;
  }
  function countKey(blockId, tierIndex) {
    return String(blockId) + ':' + String(tierIndex);
  }
  function isRejected(blockId, tierIndex) {
    return _rejectedTiers.has(rejectionKey(blockId, tierIndex));
  }
  function setRejected(blockId, tierIndex, val) {
    const k = rejectionKey(blockId, tierIndex);
    if (val) _rejectedTiers.add(k);
    else _rejectedTiers.delete(k);
  }

  function readConfigs() {
    if (_cachedConfigs && _cachedConfigs.length) return _cachedConfigs;
    const list = document.querySelectorAll('cart-drawer-auto-gift script[data-auto-gift-config]');
    const out = [];
    list.forEach((el) => {
      try {
        const cfg = JSON.parse(el.textContent || '{}');
        if (cfg && cfg.blockId && Array.isArray(cfg.tiers) && cfg.tiers.length) out.push(cfg);
      } catch (_) {}
    });
    if (out.length) _cachedConfigs = out;
    return out;
  }

  function fetchCart() {
    if (!window.routes || !window.routes.cart_url) return Promise.resolve(null);
    return fetch(`${window.routes.cart_url}.js`, { cache: 'no-store', credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }

  function qualifyingSpendCents(cart) {
    if (typeof window.qualifyingSpendCentsFromCart === 'function') {
      // Same canonical computation the progress bar uses — bar and gift
      // crossing the threshold are now mathematically identical.
      return window.qualifyingSpendCentsFromCart(cart);
    }
    if (!Array.isArray(cart.items)) return 0;
    let q = 0;
    for (const it of cart.items) {
      const p = (it && it.properties) || {};
      if (p._drawer_gift_selector) continue;
      const px =
        typeof it.original_line_price === 'number' ? it.original_line_price :
        typeof it.line_price === 'number' ? it.line_price :
        typeof it.final_line_price === 'number' ? it.final_line_price : 0;
      q += px;
    }
    return q;
  }

  function existingGiftsForTier(cart, blockId, tierIndex) {
    if (!Array.isArray(cart.items)) return [];
    return cart.items.filter((it) => {
      const p = (it && it.properties) || {};
      if (String(p._drawer_gift_selector) !== String(blockId)) return false;
      return String(p._drawer_gift_tier_index || '1') === String(tierIndex);
    });
  }

  function shuffled(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function sectionsParam() {
    const cd = document.querySelector('cart-drawer');
    if (!cd || typeof cd.getSectionsToRender !== 'function') {
      return { sections: '', sections_url: location.pathname || '/' };
    }
    const ids = cd.getSectionsToRender().map((s) => s.section || s.id).filter(Boolean);
    return { sections: ids.join(','), sections_url: location.pathname || '/' };
  }

  async function postAdd(blockId, tierIndex, variantPool) {
    const sp = sectionsParam();
    for (const variantId of variantPool) {
      const form = new FormData();
      form.append('id', String(variantId));
      form.append('quantity', '1');
      form.append('properties[_drawer_gift_selector]', String(blockId));
      form.append('properties[_drawer_gift_tier_index]', String(tierIndex));
      form.append('properties[_drawer_auto_gift]', 'true');
      if (sp.sections) form.append('sections', sp.sections);
      form.append('sections_url', sp.sections_url);
      try {
        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST', cache: 'no-store', credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: form,
        });
        if (res.ok) return await res.json();
      } catch (_) {}
    }
    return null;
  }

  async function postRemove(lineKey) {
    const sp = sectionsParam();
    try {
      const res = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST', cache: 'no-store', credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ id: lineKey, quantity: 0, sections: sp.sections, sections_url: sp.sections_url }),
      });
      if (res.ok) return await res.json();
    } catch (_) {}
    return null;
  }

  function paint(response) {
    if (!response || !response.sections) return;
    const cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.renderContents === 'function') cd.renderContents(response);
  }

  async function reconcileOnce() {
    const configs = readConfigs();
    if (configs.length === 0) return null;
    const cart = await fetchCart();
    if (!cart || !Array.isArray(cart.items)) return null;

    let lastResp = null;
    // Empty cart is a clean slate — clear every rejection flag so the
    // shopper gets a fresh chance once they re-cross any threshold.
    if (cart.items.length === 0) {
      for (const cfg of configs) {
        for (const tier of cfg.tiers) {
          setRejected(cfg.blockId, tier.i, false);
          _prevGiftCounts.set(countKey(cfg.blockId, tier.i), 0);
        }
      }
      return null;
    }

    for (const cfg of configs) {
      const blockId = cfg.blockId;
      const spend = qualifyingSpendCents(cart);

      for (const tier of cfg.tiers) {
        const threshold = Number(tier.t) || 0;
        const pool = Array.isArray(tier.v) && tier.v.length
          ? (tier.s === 'collection' ? shuffled(tier.v) : tier.v.slice())
          : [];
        const existing = existingGiftsForTier(cart, blockId, tier.i);
        const ck = countKey(blockId, tier.i);
        const prevCount = _prevGiftCounts.get(ck) || 0;

        // Fallback manual-remove detection:
        // If this tier had a gift line previously, now has none, and spend is
        // still above threshold, treat it as user intent and block re-add.
        if (spend >= threshold && prevCount > 0 && existing.length === 0) {
          setRejected(blockId, tier.i, true);
        }
        _prevGiftCounts.set(ck, existing.length);

        if (spend >= threshold) {
          if (existing.length === 0 && pool.length > 0) {
            // Honor user rejection: × on the gift line set the flag.
            // Skip add until spend drops below threshold (which clears it).
            if (isRejected(blockId, tier.i)) continue;
            const r = await postAdd(blockId, tier.i, pool);
            if (r) lastResp = r;
          } else if (existing.length > 1) {
            for (let i = 1; i < existing.length; i++) {
              const r = await postRemove(existing[i].key);
              if (r) lastResp = r;
            }
          }
        } else {
          // Below threshold: clear the rejection flag (re-crossing the
          // threshold should re-offer the gift), and remove any existing
          // gift lines for this tier.
          setRejected(blockId, tier.i, false);
          _prevGiftCounts.set(ck, existing.length);
          for (const line of existing) {
            const r = await postRemove(line.key);
            if (r) lastResp = r;
          }
        }
      }
    }
    return lastResp;
  }

  async function drain() {
    if (_inFlight) { _pending = true; return; }
    _inFlight = true;
    try {
      do {
        _pending = false;
        const resp = await reconcileOnce();
        if (resp) paint(resp);
      } while (_pending);
    } finally {
      _inFlight = false;
    }
  }

  function trigger() { drain(); }

  // cart.js calls this from the × button on the gift's remove control.
  // Set the session-scoped rejection flag so the next reconcile keeps
  // the gift removed instead of re-adding it on the spot.
  window.__autoGiftRejectTier = function (blockId, tierIndex) {
    if (blockId == null || tierIndex == null) return;
    setRejected(String(blockId), String(tierIndex), true);
  };

  document.addEventListener('cart:updated', trigger);

  if (typeof window.subscribe === 'function') {
    const evt =
      (typeof window.PUB_SUB_EVENTS !== 'undefined' && window.PUB_SUB_EVENTS.cartUpdate) ||
      'cart-update';
    window.subscribe(evt, trigger);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trigger, { once: true });
  } else {
    trigger();
  }
})();
